import { neutral, radius, semantic } from "../../theme/tokens";

/**
 * Nhãn trạng thái — MỘT bảng màu duy nhất cho toàn CMS.
 *
 * ─── LUẬT MÀU (đọc trước khi thêm tag mới) ───────────────────────────────
 *
 * Màu CHỈ dùng để báo TRẠNG THÁI. Không dùng màu để phân biệt LOẠI.
 *
 *   ✅ "Đang hoạt động" xanh · "Đã khóa" đỏ · "Chờ duyệt" vàng
 *   ❌ "Vật phẩm" xanh dương · "Data" cam   -> đây là LOẠI, phải dùng `default`
 *   ❌ "60 điểm" bọc trong tag xanh          -> đây là SỐ, đừng bọc tag
 *
 * Vì sao: audit thấy có màn hiện 4 tag cạnh nhau với 4 màu khác nhau mà không
 * cái nào là trạng thái (VẬT PHẨM / Còn 117 lượt / 60 điểm / Không). Mắt người
 * đọc màu như một tín hiệu; khi mọi thứ đều có màu thì không còn gì nổi bật và
 * màn hình trông rối. Giữ màu cho đúng chỗ cần chú ý.
 *
 * ─── BẢNG TONE ───────────────────────────────────────────────────────────
 *
 *   success  hoàn tất, đang hoạt động, còn hàng, điểm tăng
 *   danger   thất bại, đã khóa, hết hạn, hết hàng, điểm giảm
 *   warning  cần chú ý, chờ duyệt, sắp hết
 *   info     đang chạy / đang xử lý (trạng thái trung gian, KHÔNG phải "loại")
 *   default  mọi nhãn không mang trạng thái: loại, nguồn, phân nhóm
 *
 * KHÔNG dùng `<Tag color="...">` của antd cho trạng thái nữa — bảng preset của
 * antd trộn hai hệ (`blue`/`green` vs `error`/`success`/`processing`) render ra
 * hai kiểu viền khác nhau, chính là nguồn gốc của sự lộn xộn.
 *
 * @example
 * <StatusTag tone="success" dot>Đang hoạt động</StatusTag>
 * <StatusTag>Vật phẩm</StatusTag>            // loại -> để mặc định (xám)
 */
const TONES = {
  success: { fg: semantic.success, bg: semantic.successSubtle },
  warning: { fg: semantic.warning, bg: semantic.warningSubtle },
  danger: { fg: semantic.danger, bg: semantic.dangerSubtle },
  info: { fg: semantic.info, bg: semantic.infoSubtle },
  default: { fg: neutral[600], bg: neutral[100] },
  // Bí danh của `default` — vốn là tên tone bên `prizeMeta.js` (màn Gamification).
  neutral: { fg: neutral[600], bg: neutral[100] },
};

/*
 * Tên màu preset của antd -> tone, để những chỗ đang giữ màu trong một BẢNG
 * TRẠNG THÁI (`STATUS_META`, `getStatusMeta`, ...) chuyển sang được mà không phải
 * viết lại cả bảng: `<StatusTag tone={meta.color}>`.
 *
 * Chỉ dùng cho bảng TRẠNG THÁI. Bảng LOẠI thì sửa thẳng bảng về `default` —
 * ánh xạ ở đây sẽ biến `blue`/`purple` thành `info`, đúng cho trạng thái nhưng
 * sai cho loại.
 */
const PRESET_TONE = {
  success: "success", green: "success", lime: "success",
  error: "danger", red: "danger", volcano: "danger", magenta: "danger",
  warning: "warning", gold: "warning", orange: "warning", yellow: "warning",
  processing: "info", blue: "info", cyan: "info", geekblue: "info",
};

export default function StatusTag({ tone = "default", dot = false, children, style }) {
  const t = TONES[tone] || TONES[PRESET_TONE[tone]] || TONES.default;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: radius.pill,
        background: t.bg,
        color: t.fg,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "18px",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: t.fg,
            flexShrink: 0,
          }}
        />
      )}
      {children}
    </span>
  );
}
